import { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { 
  Flame, Activity, AlertTriangle, Clock, Sun, Moon, 
  Calendar, ChevronLeft, ChevronRight, Users, Briefcase,
  ArrowDownCircle, ArrowUpCircle, RefreshCw, Box
} from 'lucide-react';

// All furnace definitions
const ALL_FURNACES = [
  // Big Furnaces C1-C15
  ...Array.from({ length: 15 }, (_, i) => ({ id: `C${i + 1}`, name: `C${i + 1}`, type: 'Big', capacity: 500 })),
  // Small Furnaces SM1-SM7
  ...Array.from({ length: 7 }, (_, i) => ({ id: `SM${i + 1}`, name: `SM${i + 1}`, type: 'Small', capacity: 200 })),
];

const BIG_FURNACES = ALL_FURNACES.filter(f => f.type === 'Big');
const SMALL_FURNACES = ALL_FURNACES.filter(f => f.type === 'Small');

function FurnaceDashboard() {
  const { API } = useContext(AppContext);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedShift, setSelectedShift] = useState('Day');
  const [productionData, setProductionData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCalendar, setShowCalendar] = useState(false);

  useEffect(() => {
    fetchProductionData();
  }, [selectedDate, selectedShift]);

  const fetchProductionData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/production`);
      const allEntries = response.data || [];
      // Filter by selected date and shift
      const filtered = allEntries.filter(entry => 
        entry.date === selectedDate && entry.shift === selectedShift
      );
      setProductionData(filtered);
    } catch (error) {
      console.error('Error fetching production data:', error);
    }
    setLoading(false);
  };

  // Get furnace data from production entries
  const getFurnaceData = (furnaceId) => {
    const entries = productionData.filter(e => e.furnace === furnaceId);
    if (entries.length === 0) {
      return {
        status: 'Closed',
        operators: '-',
        contractor: '-',
        totalInput: 0,
        totalOutput: 0,
        fgType: '-'
      };
    }

    // Aggregate data from all entries for this furnace
    const totalInput = entries.reduce((sum, e) => sum + (parseFloat(e.totalInput) || 0), 0);
    const totalOutput = entries.reduce((sum, e) => sum + (parseFloat(e.totalOutput) || 0), 0);
    const operators = entries[0]?.operatorNames || '-';
    const contractor = entries[0]?.contractor || entries[0]?.contractorName || '-';
    const fgType = entries[0]?.fgType || '-';
    
    // Determine status based on entries
    const hasRunning = entries.some(e => e.status === 'Running' || e.status === 'Open');
    const status = hasRunning ? 'Running' : 'Closed';

    return {
      status,
      operators,
      contractor,
      totalInput,
      totalOutput,
      fgType
    };
  };

  // Calculate summary stats
  const summaryStats = {
    running: ALL_FURNACES.filter(f => getFurnaceData(f.id).status === 'Running').length,
    closed: ALL_FURNACES.filter(f => getFurnaceData(f.id).status === 'Closed').length,
    totalInput: productionData.reduce((sum, e) => sum + (parseFloat(e.totalInput) || 0), 0),
    totalOutput: productionData.reduce((sum, e) => sum + (parseFloat(e.totalOutput) || 0), 0),
  };

  // Date navigation functions
  const changeDate = (days) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + days);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Running': return 'border-green-500 bg-gradient-to-br from-green-50 to-emerald-50';
      case 'Closed': return 'border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100';
      default: return 'border-gray-300 bg-gray-50';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Running': 
        return (
          <span className="flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
            <Activity size={10} className="animate-pulse" />
            Running
          </span>
        );
      case 'Closed': 
        return (
          <span className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
            <Clock size={10} />
            Closed
          </span>
        );
      default: return null;
    }
  };

  // Furnace Card Component
  const FurnaceCard = ({ furnace }) => {
    const data = getFurnaceData(furnace.id);
    
    return (
      <div
        className={`rounded-xl border-2 p-4 transition hover:shadow-lg ${getStatusColor(data.status)}`}
        data-testid={`furnace-card-${furnace.id}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Flame size={20} className={data.status === 'Running' ? 'text-orange-500' : 'text-gray-400'} />
            <span className="font-bold text-xl text-gray-900">{furnace.name}</span>
          </div>
          {getStatusBadge(data.status)}
        </div>
        
        {/* Details */}
        <div className="space-y-2">
          {/* Operators */}
          <div className="flex items-start gap-2">
            <Users size={14} className="text-gray-400 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs text-gray-500">Operators</p>
              <p className="text-sm font-medium text-gray-800 truncate" title={data.operators}>
                {data.operators}
              </p>
            </div>
          </div>
          
          {/* Contractor */}
          <div className="flex items-start gap-2">
            <Briefcase size={14} className="text-gray-400 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs text-gray-500">Contractor</p>
              <p className="text-sm font-medium text-gray-800 truncate" title={data.contractor}>
                {data.contractor}
              </p>
            </div>
          </div>
          
          {/* FG Type */}
          <div className="flex items-start gap-2">
            <Box size={14} className="text-gray-400 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs text-gray-500">FG Type</p>
              <p className="text-sm font-medium text-gray-800">
                {data.fgType !== '-' ? (
                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                    {data.fgType}
                  </span>
                ) : '-'}
              </p>
            </div>
          </div>
          
          {/* Input/Output Row */}
          <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-200">
            <div className="flex items-center gap-1.5">
              <ArrowDownCircle size={14} className="text-blue-500" />
              <div>
                <p className="text-xs text-gray-500">Input</p>
                <p className="text-sm font-bold text-blue-600">{data.totalInput.toLocaleString()} kg</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <ArrowUpCircle size={14} className="text-green-500" />
              <div>
                <p className="text-xs text-gray-500">Output</p>
                <p className="text-sm font-bold text-green-600">{data.totalOutput.toLocaleString()} kg</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="furnace-dashboard-page">
      {/* Header with Calendar */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Title */}
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Flame className="text-orange-500" size={28} />
              Furnace Dashboard
            </h1>
            <p className="text-gray-600 mt-1">Real-time status of all 22 furnaces</p>
          </div>
          
          {/* Date & Shift Selection */}
          <div className="flex items-center gap-4 flex-wrap">
            {/* Date Picker */}
            <div className="flex items-center gap-2 bg-gray-100 rounded-xl p-1">
              <button
                onClick={() => changeDate(-1)}
                className="p-2 hover:bg-white rounded-lg transition"
                title="Previous Day"
              >
                <ChevronLeft size={18} className="text-gray-600" />
              </button>
              
              <div className="relative">
                <button
                  onClick={() => setShowCalendar(!showCalendar)}
                  className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm border border-gray-200 hover:border-orange-300 transition"
                  data-testid="date-picker-btn"
                >
                  <Calendar size={16} className="text-orange-500" />
                  <span className="font-medium text-gray-800">{formatDate(selectedDate)}</span>
                </button>
                
                {showCalendar && (
                  <div className="absolute top-full mt-2 left-0 z-50 bg-white rounded-xl shadow-xl border border-gray-200 p-3">
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => { setSelectedDate(e.target.value); setShowCalendar(false); }}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                      data-testid="date-input"
                    />
                  </div>
                )}
              </div>
              
              <button
                onClick={() => changeDate(1)}
                className="p-2 hover:bg-white rounded-lg transition"
                title="Next Day"
              >
                <ChevronRight size={18} className="text-gray-600" />
              </button>
            </div>
            
            {/* Shift Toggle */}
            <div className="flex bg-gray-100 rounded-xl p-1">
              {['Day', 'Night'].map((shift) => (
                <button
                  key={shift}
                  onClick={() => setSelectedShift(shift)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 ${
                    selectedShift === shift
                      ? 'bg-white text-orange-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  data-testid={`shift-${shift.toLowerCase()}-btn`}
                >
                  {shift === 'Day' ? <Sun size={16} /> : <Moon size={16} />}
                  {shift} Shift
                </button>
              ))}
            </div>
            
            {/* Refresh Button */}
            <button
              onClick={fetchProductionData}
              className="p-2 bg-orange-100 text-orange-600 rounded-lg hover:bg-orange-200 transition"
              title="Refresh Data"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-2">
            <Activity className="opacity-70" size={18} />
            <p className="text-sm opacity-80">Running</p>
          </div>
          <p className="text-3xl font-bold mt-1">{summaryStats.running}</p>
          <p className="text-xs opacity-70">of 22 furnaces</p>
        </div>
        <div className="bg-gradient-to-br from-gray-500 to-gray-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-2">
            <Clock className="opacity-70" size={18} />
            <p className="text-sm opacity-80">Closed</p>
          </div>
          <p className="text-3xl font-bold mt-1">{summaryStats.closed}</p>
          <p className="text-xs opacity-70">not operating</p>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-2">
            <ArrowDownCircle className="opacity-70" size={18} />
            <p className="text-sm opacity-80">Total Input</p>
          </div>
          <p className="text-3xl font-bold mt-1">{(summaryStats.totalInput / 1000).toFixed(2)} T</p>
          <p className="text-xs opacity-70">raw material</p>
        </div>
        <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-xl p-4 text-white">
          <div className="flex items-center gap-2">
            <ArrowUpCircle className="opacity-70" size={18} />
            <p className="text-sm opacity-80">Total Output</p>
          </div>
          <p className="text-3xl font-bold mt-1">{(summaryStats.totalOutput / 1000).toFixed(2)} T</p>
          <p className="text-xs opacity-70">finished goods</p>
        </div>
      </div>

      {/* Status Legend */}
      <div className="flex items-center gap-6 px-4">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
          <span className="text-sm text-gray-600">Running</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-gray-400"></div>
          <span className="text-sm text-gray-600">Closed</span>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <RefreshCw size={24} className="animate-spin text-orange-500" />
          <span className="ml-2 text-gray-600">Loading furnace data...</span>
        </div>
      )}

      {/* Big Furnaces Section */}
      {!loading && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <Flame size={18} className="text-blue-600" />
              Big Furnaces (C1 - C15) — 500 kg Capacity Each
            </h3>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {BIG_FURNACES.map((furnace) => (
                <FurnaceCard key={furnace.id} furnace={furnace} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Small Furnaces Section */}
      {!loading && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50">
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <Flame size={18} className="text-purple-600" />
              Small Furnaces (SM1 - SM7) — 200 kg Capacity Each
            </h3>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4">
              {SMALL_FURNACES.map((furnace) => (
                <FurnaceCard key={furnace.id} furnace={furnace} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* No Data Message */}
      {!loading && productionData.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
          <AlertTriangle size={32} className="mx-auto text-yellow-500 mb-2" />
          <p className="text-yellow-800 font-medium">No production data found</p>
          <p className="text-yellow-600 text-sm mt-1">
            No production entries for {formatDate(selectedDate)} - {selectedShift} Shift
          </p>
        </div>
      )}
    </div>
  );
}

export default FurnaceDashboard;
